import { useAtom, useAtomValue } from 'jotai';

import { useGetTelehealthSessionInfo } from '../../hooks';
import { appointmentInfoAtom, sessionInfoAtom } from '../../state';
import { AppPlatform } from '../../types';
import { nameToInitials } from '../../utils';
import { inSessionPatientInformationAtom, providerShortIdAtom, selectedClientUserShortIdAtom } from '../state';
import { useGetPatientByShortId } from '../useGetPatientByShortId';
import { useGetCurrentIntentionQuery } from './useGetCurrentIntentionQuery';
import { type IntentionUse, useIntentionUse } from './useIntentionUse';
import { useMomentumPilotPatientAccessQuery } from './useMomentumPilotPatientAccessQuery';

/**
 * Hook to determine the current intention setting state for a patient
 * Combines data from multiple atoms and calculates the appropriate UI state
 *
 * @returns Object containing intention state and patient data, or undefined if no patient
 */
export function useIntentionState():
  | {
      use: IntentionUse;
      patientShortId: string;
      preferredName: string;
      initials: string;
      clientUserShortId: string;
      providerShortId: string;
      appointmentShortId: string;
      providerImage: string;
      isLoading: boolean;
    }
  | undefined {
  const appointmentInfo = useAtomValue(appointmentInfoAtom);
  const [inSessionPatientInfo, setInSessionPatientInfo] = useAtom(inSessionPatientInformationAtom);
  const providerShortId = useAtomValue(providerShortIdAtom) ?? '';
  const clientUserShortId = useAtomValue(selectedClientUserShortIdAtom) ?? '';
  const sessionInfo = useAtomValue(sessionInfoAtom);

  const patientShortId = inSessionPatientInfo?.patientShortId ?? '';

  const { loading: sessionInfoLoading } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const { loading: patientDataLoading } = useGetPatientByShortId(patientShortId);

  // TODO: [MOMENT-284]: Remove fallbackAppVersion code once iOS client fix is done or pilot is over
  const fallbackAppVersion = inSessionPatientInfo?.appPlatform === AppPlatform.IOS_APP ? '1.34.1' : '';

  const { pilotAccess, loading: pilotAccessLoading } = useMomentumPilotPatientAccessQuery(
    patientShortId,
    'intention-v1',
    inSessionPatientInfo?.appVersion ?? fallbackAppVersion,
  );

  const intentionCompletedAt = inSessionPatientInfo?.intentionCompletedAt;
  const intentionPromptSentAt = inSessionPatientInfo?.intentionPromptSentAt;
  const appointmentEndTime = appointmentInfo?.timeEnd;
  const appointmentShortId = appointmentInfo?.shortId ?? '';

  const fullName = inSessionPatientInfo?.name ?? '';
  const preferredName = fullName.split(' ')[0] || '';
  const initials = nameToInitials(fullName);

  // Check if there's already an intention for this appointment
  // This can happen if the session was disconnected
  const { loading: intentionLoading } = useGetCurrentIntentionQuery(
    clientUserShortId,
    providerShortId,
    appointmentShortId,
    {
      skip: !appointmentShortId,
      onCompleted: (data) => {
        const intentionCreatedAt = data?.intentionHistory?.currentSessionIntention?.createdAt;
        if (intentionCreatedAt && !intentionCompletedAt) {
          setInSessionPatientInfo((prev) => {
            if (!prev || prev.patientShortId !== patientShortId) return prev;

            return {
              ...prev,
              intentionCompletedAt: intentionCreatedAt,
            };
          });
        }
      },
    },
  );

  const use = useIntentionUse(intentionCompletedAt, intentionPromptSentAt, appointmentEndTime);

  if (!patientShortId || !inSessionPatientInfo || !pilotAccess?.enabled) {
    return undefined;
  }

  return {
    use,
    patientShortId,
    preferredName,
    initials,
    clientUserShortId,
    providerShortId,
    appointmentShortId,
    providerImage: sessionInfo?.provider.image ?? '',
    isLoading: sessionInfoLoading || pilotAccessLoading || intentionLoading || patientDataLoading,
  };
}
